import { useRef } from "react";
import { useParams, Navigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { BRANDS, VIDEOS } from "../data/brands";
import { MaskedLines, Reveal } from "../components/Reveal";
import Header from "../components/Header";
import Footer from "../components/Footer";
import BrandMarquee from "../components/Marquee";
import ContactForm from "../components/ContactForm";
import StatsSection from "../components/StatsSection";
import HeroVideoBg from "../components/HeroVideoBg";

export default function BrandSite() {
  const { brandKey } = useParams();
  const brand = BRANDS[brandKey];
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.1]);

  if (!brand) return <Navigate to="/" replace />;

  return (
    <div className="bg-dejure-ivory">
      <Header brandKey={brand.key} brandName={brand.name} />

      {/* Hero */}
      <section ref={heroRef} className="relative min-h-screen flex items-center overflow-hidden" data-testid="brand-hero">
        <motion.div style={{ y: imgY, scale: imgScale }} className="absolute inset-0 z-0">
          <HeroVideoBg src={VIDEOS[brand.key].src} poster={VIDEOS[brand.key].poster} />
          <div className="absolute inset-0 bg-gradient-to-r from-dejure-ivory via-dejure-ivory/90 to-dejure-ivory/35" />
          <div className="absolute inset-0 bg-gradient-to-t from-dejure-ivory via-dejure-ivory/10 to-transparent" />
        </motion.div>

        <div className="relative z-10 mx-auto max-w-[1400px] w-full px-6 md:px-10 pt-32">
          <div className="max-w-2xl">
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2, duration: 1 }}
              className="font-accent text-xs uppercase tracking-[0.3em] text-dejure-purple"
            >
              {brand.eyebrow}
            </motion.p>
            <h1 className="mt-6 font-serif text-5xl sm:text-6xl lg:text-8xl leading-[0.88] tracking-tighter text-dejure-ink">
              <MaskedLines lines={brand.titleLines} delay={0.25} />
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.95, duration: 0.9 }}
              className="mt-9 max-w-lg font-sans text-base md:text-lg leading-relaxed text-dejure-ink/70"
            >
              {brand.heroSub}
            </motion.p>
            <motion.a
              href="#services"
              data-testid="hero-scroll-cta"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.2, duration: 0.8 }}
              className="group mt-10 inline-flex items-center gap-3 font-accent text-xs uppercase tracking-[0.2em] text-dejure-ink hover:text-dejure-purple transition-colors duration-300"
            >
              Explore our work
              <ArrowDown size={16} className="transition-transform duration-300 group-hover:translate-y-1" />
            </motion.a>
          </div>
        </div>
      </section>

      {/* About */}
      <section id="about" className="border-t border-dejure-silver/30 bg-white" data-testid="brand-about">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 py-24 md:py-36 grid lg:grid-cols-[0.8fr_1.2fr] gap-12 md:gap-20">
          <Reveal>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tighter text-dejure-ink">
              {brand.about.heading}
            </h2>
          </Reveal>
          <div>
            <Reveal delay={0.1}>
              <p className="font-serif text-2xl md:text-3xl leading-snug text-dejure-ink/90">
                {brand.about.body}
              </p>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-8 max-w-2xl font-sans text-base md:text-lg leading-relaxed text-dejure-ink/60">
                {brand.about.body2}
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      <BrandMarquee items={brand.marquee} />

      {/* Services */}
      <section id="services" className="mx-auto max-w-[1400px] px-6 md:px-10 py-24 md:py-36" data-testid="brand-services">
        <Reveal>
          <div className="border-b border-dejure-silver/30 pb-8">
            <p className="font-accent text-xs uppercase tracking-[0.25em] text-dejure-purple">What we offer</p>
            <h2 className="mt-4 font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tighter text-dejure-ink">
              {brand.servicesTitle}
            </h2>
          </div>
        </Reveal>

        <div className="mt-4 grid md:grid-cols-2 lg:grid-cols-3">
          {brand.services.map((s, i) => (
            <Reveal key={s.t} delay={(i % 3) * 0.08}>
              <div
                data-testid={`service-${i}`}
                className="group h-full border-b border-dejure-silver/25 p-8 md:p-10 lg:[&:not(:nth-child(3n))]:border-r hover:bg-dejure-mist transition-colors duration-500"
              >
                <span className="font-serif text-2xl text-dejure-silver">{String(i + 1).padStart(2, "0")}</span>
                <h3 className="mt-5 font-serif text-2xl md:text-3xl tracking-tight text-dejure-ink group-hover:text-dejure-purple transition-colors duration-300">
                  {s.t}
                </h3>
                <p className="mt-4 font-sans text-sm md:text-base leading-relaxed text-dejure-ink/55">{s.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Philosophy */}
      <section id="approach" className="bg-dejure-purple-dark text-white" data-testid="brand-philosophy">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 py-24 md:py-36">
          <Reveal>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tighter">
              {brand.philosophyTitle}
            </h2>
          </Reveal>
          <div className="mt-16 grid gap-px md:grid-cols-3 bg-white/10">
            {brand.philosophy.map((p, i) => (
              <Reveal key={p.n} delay={i * 0.1}>
                <div className="h-full bg-dejure-purple-dark p-8 md:p-10">
                  <span className="font-serif text-6xl md:text-7xl text-dejure-silver/60">{p.n}</span>
                  <h3 className="mt-6 font-serif text-3xl tracking-tight">{p.t}</h3>
                  <p className="mt-4 font-sans text-sm md:text-base leading-relaxed text-white/65">{p.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <StatsSection variant="light" />

      <ContactForm
        brand={brand.key}
        heading={`Work with ${brand.name.replace("De Jure ", "")}`}        blurb={`Reach out to the ${brand.name} team. We respond with considered, confidential guidance tailored to your goals.`}
      />
      <Footer brandKey={brand.key} />
    </div>
  );
}
